/**
 * 대한민국 공휴일 표 — [📅 일정] 화면의 휴일 표시에 쓴다
 * (v2.171.0 · 2026-09-14 · 사용자 요청 「캘린더 휴일 표시」).
 *
 * ## 왜 표로 들고 있나 (사용자 결정 2026-09-14)
 *
 * 설날·추석·부처님오신날은 **음력**이라 해마다 날짜가 바뀌고, 공휴일이 토·일과 겹치면
 * **대체공휴일**이 붙는다 — 계산으로 뽑으려면 음력 변환표가 있어야 한다. 정부 API
 * (공공데이터포털 특일정보)를 쓰는 길도 있지만 **인증키를 따로 발급받아야** 하고 그쪽이
 * 멈추면 휴일이 통째로 안 보인다. 그래서 **포털 안에 적어 두는 쪽**을 골랐다.
 *
 * ## 대체공휴일 규칙 (관공서의 공휴일에 관한 규정 제3조) — 이 표를 손볼 때 기준
 *
 * | 공휴일 | 대체가 붙는 때 |
 * |---|---|
 * | 설날·추석 연휴 | **일요일**과 겹칠 때만 (⛔토요일은 안 붙는다) |
 * | 어린이날 | 토요일 **또는** 일요일 |
 * | 3·1절 · 광복절 · 개천절 · 한글날 · 부처님오신날 · 성탄절 | 토요일 **또는** 일요일 |
 * | 제헌절(2026~) · 노동절(2026~) | 토요일 **또는** 일요일 (2027 월력요항: 5/1 토 → 5/3 · 7/17 토 → 7/19) |
 * | 신정(1/1) · 현충일 | **안 붙는다** |
 *
 * ⭐실제로 2026년 추석 9/26 은 **토요일인데 대체가 없고**, 같은 해 광복절 8/15 도 토요일인데
 *   **대체가 있다**(8/17). 규칙이 공휴일마다 다르기 때문이다 — 「토·일이면 무조건 대체」로
 *   외우면 틀린다.
 *
 * ## 표가 끝나면
 *
 * `HOLIDAY_LAST_YEAR` 이후는 아무것도 표시하지 않는다. 화면이 그 해에 가까워지면
 * **안내를 띄운다**(`holidayTableEndingSoon`) — 조용히 비어 보이면 「휴일 표시가 고장났다」가 된다.
 * ⭐다음 해를 채울 때는 **음력 날짜와 대체공휴일을 반드시 확인**하고 넣을 것. 짐작 금지.
 */

export interface Holiday {
  /** YYYY-MM-DD */
  date: string;
  name: string;
  /** 대체공휴일·임시공휴일이면 true — 화면에서 조금 다르게 적는다 */
  substitute?: boolean;
}

/** 이 표가 담고 있는 마지막 해 */
export const HOLIDAY_LAST_YEAR = 2028;

/**
 * 연도별 공휴일. ⚠날짜·요일·대체 여부는 2026-09-14 에 전수 대조했다.
 *   ⭐2026-10-06 다시 대조(시나리오 점검 · 2027년 월력요항 = 우주항공청 2026-06-29 발표 · 공휴일법 개정):
 *     ① 2027 설날이 하루 밀려 있었다 — 설날은 2/7(일) · 연휴 2/6~2/8 · 대체 2/9(화) (평일 2/10 을 쉬는 날로 두고 있었다)
 *     ② 2026 부터 **노동절(5/1)**·**제헌절(7/17)** 이 공휴일(노동절 = 법률 제21543호 2026-05-01 시행 · 제헌절 = 2026-02-03 국무회의 의결)
 *     ③ 2026-06-03 제9회 전국동시지방선거(선거일 = 공휴일)가 빠져 있었다
 *     2027 은 공식 집계(공휴일·대체공휴일 24일 · 그중 일요일과 겹친 4일)와 건수가 맞는다.
 *   2025: 임시공휴일 2건 포함(1/27 설 연휴 임시공휴일 · 6/3 제21대 대통령선거).
 *   ⭐2028 은 2026-10-26 에 넣었다 — **공식 월력요항(2027년 6월께 발표) 전**이라 음력은 한국 음력(ICU dangi 달력)으로 계산해 대조했다:
 *     ① 설날 = **1/27(목)** — 중국 음력으로는 1/26 이다(합삭이 한국 1/27 0시 12분 · 중국 1/26 23시 12분). 「1/26」 이라 적은 사이트가 많다 — 짐작 금지.
 *     ② 추석 10/3(화) = 개천절과 같은 날 → 대체 10/5(목) 하루(2017 년과 같은 모양) · 부처님오신날 5/2(화)
 *     ③ 제23대 국회의원선거 4/12(수 · 임기만료일 전 50일 뒤 첫 수요일) · 대체 붙는 토·일 겹침은 없다(신정 1/1 토는 대체 없음)
 *     ⚠임시공휴일·법 개정은 모른다 — **월력요항이 나오면 다시 대조**할 것.
 */
const TABLE: Record<number, Holiday[]> = {
  2025: [
    { date: '2025-01-01', name: '신정' },
    { date: '2025-01-27', name: '임시공휴일', substitute: true },
    { date: '2025-01-28', name: '설날 연휴' },
    { date: '2025-01-29', name: '설날' },
    { date: '2025-01-30', name: '설날 연휴' },
    { date: '2025-03-01', name: '3·1절' },
    { date: '2025-03-03', name: '3·1절 대체공휴일', substitute: true },
    { date: '2025-05-05', name: '어린이날·부처님오신날' },
    { date: '2025-05-06', name: '대체공휴일', substitute: true },
    { date: '2025-06-03', name: '제21대 대통령선거', substitute: true },
    { date: '2025-06-06', name: '현충일' },
    { date: '2025-08-15', name: '광복절' },
    { date: '2025-10-03', name: '개천절' },
    { date: '2025-10-05', name: '추석 연휴' },
    { date: '2025-10-06', name: '추석' },
    { date: '2025-10-07', name: '추석 연휴' },
    { date: '2025-10-08', name: '추석 대체공휴일', substitute: true },
    { date: '2025-10-09', name: '한글날' },
    { date: '2025-12-25', name: '성탄절' },
  ],
  2026: [
    { date: '2026-01-01', name: '신정' },
    { date: '2026-02-16', name: '설날 연휴' },
    { date: '2026-02-17', name: '설날' },
    { date: '2026-02-18', name: '설날 연휴' },
    { date: '2026-03-01', name: '3·1절' },
    { date: '2026-03-02', name: '3·1절 대체공휴일', substitute: true },
    { date: '2026-05-01', name: '노동절' },
    { date: '2026-05-05', name: '어린이날' },
    { date: '2026-05-24', name: '부처님오신날' },
    { date: '2026-05-25', name: '부처님오신날 대체공휴일', substitute: true },
    { date: '2026-06-03', name: '제9회 전국동시지방선거', substitute: true },
    { date: '2026-06-06', name: '현충일' },
    { date: '2026-07-17', name: '제헌절' },
    { date: '2026-08-15', name: '광복절' },
    { date: '2026-08-17', name: '광복절 대체공휴일', substitute: true },
    // ⛔추석 9/26 은 토요일이지만 **대체공휴일이 없다** — 설·추석은 일요일과 겹칠 때만 붙는다
    { date: '2026-09-24', name: '추석 연휴' },
    { date: '2026-09-25', name: '추석' },
    { date: '2026-09-26', name: '추석 연휴' },
    { date: '2026-10-03', name: '개천절' },
    { date: '2026-10-05', name: '개천절 대체공휴일', substitute: true },
    { date: '2026-10-09', name: '한글날' },
    { date: '2026-12-25', name: '성탄절' },
  ],
  2027: [
    { date: '2027-01-01', name: '신정' },
    { date: '2027-02-06', name: '설날 연휴' },
    { date: '2027-02-07', name: '설날' },
    { date: '2027-02-08', name: '설날 연휴' },
    { date: '2027-02-09', name: '설날 대체공휴일', substitute: true },
    { date: '2027-03-01', name: '3·1절' },
    { date: '2027-05-01', name: '노동절' },
    { date: '2027-05-03', name: '노동절 대체공휴일', substitute: true },
    { date: '2027-05-05', name: '어린이날' },
    { date: '2027-05-13', name: '부처님오신날' },
    { date: '2027-06-06', name: '현충일' },
    { date: '2027-07-17', name: '제헌절' },
    { date: '2027-07-19', name: '제헌절 대체공휴일', substitute: true },
    { date: '2027-08-15', name: '광복절' },
    { date: '2027-08-16', name: '광복절 대체공휴일', substitute: true },
    { date: '2027-09-14', name: '추석 연휴' },
    { date: '2027-09-15', name: '추석' },
    { date: '2027-09-16', name: '추석 연휴' },
    { date: '2027-10-03', name: '개천절' },
    { date: '2027-10-04', name: '개천절 대체공휴일', substitute: true },
    { date: '2027-10-09', name: '한글날' },
    { date: '2027-10-11', name: '한글날 대체공휴일', substitute: true },
    { date: '2027-12-25', name: '성탄절' },
    { date: '2027-12-27', name: '성탄절 대체공휴일', substitute: true },
  ],
  2028: [
    { date: '2028-01-01', name: '신정' },
    { date: '2028-01-26', name: '설날 연휴' },
    { date: '2028-01-27', name: '설날' },
    { date: '2028-01-28', name: '설날 연휴' },
    { date: '2028-03-01', name: '3·1절' },
    { date: '2028-04-12', name: '제23대 국회의원선거', substitute: true },
    { date: '2028-05-01', name: '노동절' },
    { date: '2028-05-02', name: '부처님오신날' },
    { date: '2028-05-05', name: '어린이날' },
    { date: '2028-06-06', name: '현충일' },
    { date: '2028-07-17', name: '제헌절' },
    { date: '2028-08-15', name: '광복절' },
    { date: '2028-10-02', name: '추석 연휴' },
    { date: '2028-10-03', name: '추석·개천절' },
    { date: '2028-10-04', name: '추석 연휴' },
    { date: '2028-10-05', name: '대체공휴일', substitute: true },
    { date: '2028-10-09', name: '한글날' },
    { date: '2028-12-25', name: '성탄절' },
  ],
};

/** 날짜(YYYY-MM-DD) → 공휴일 (아니면 null) 로 바로 찾는 표 */
const BY_DATE: Map<string, Holiday> = new Map(
  Object.values(TABLE).flat().map((h) => [h.date, h]),
);

/** 그 날이 공휴일인가 — 아니면 null */
export function holidayOf(isoDate: string): Holiday | null {
  return BY_DATE.get(isoDate) ?? null;
}

/** 그 달(YYYY-MM)의 공휴일 목록 */
export function holidaysOfMonth(year: number, month1to12: number): Holiday[] {
  const prefix = `${year}-${String(month1to12).padStart(2, '0')}-`;
  return (TABLE[year] ?? []).filter((h) => h.date.startsWith(prefix));
}

/**
 * 표가 곧 끝나는가 — 보고 있는 해가 표의 마지막 해 이상이면 true.
 * ⭐조용히 비어 보이는 것을 막는다. 「휴일이 원래 없는 달」과 「표가 없어서 안 보이는 달」은
 *   화면에서 똑같아 보이는데, 뒤쪽은 **고장**이다.
 */
export function holidayTableEndingSoon(viewingYear: number): boolean {
  return viewingYear >= HOLIDAY_LAST_YEAR;
}

/** 표에 아예 없는 해인가 (그 해는 휴일을 하나도 못 그린다) */
export function holidayTableMissing(viewingYear: number): boolean {
  return !TABLE[viewingYear];
}
